<template>
  <div class="emh-dealer-locator-selector">
    <wb-checkbox-control v-show="showApplyAllowedList" class="wb-margin-bottom-xs">
      <label>
        <input v-model="applyAllowedList" type="checkbox" name="allowedList" />
        {{ $t('dcp.dlc.applyLocationFilter') }}
      </label>
    </wb-checkbox-control>

    <dealer-locator
      container-id="emh-dealer-locator-selector__widget-container"
      :interactive="interactive"
      :preload="preload"
      :allowed-list="vendorList"
      :hidden-fields="hiddenFields"
      remove-margins
      :loading="loading"
      @dealer-selected="dealerSelected"
    ></dealer-locator>

    <div v-show="showErrorMessage">
      <p class="emh-dealer-locator-selector-popup__copy">{{ $t('dcp.vendorContext.selector.popupError.copy') }}</p>
      <button
        class="wb-button wb-button--primary wb-button--large emh-dealer-locator-selector-popup__buttons"
        @click="showErrorMessage = false"
      >
        {{ $t('dcp.vendorContext.selector.popupError.cta') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, ComputedRef, defineComponent, PropType, Ref, ref, toRef } from 'vue';
import intersection from 'lodash/intersection';
// Components
import DealerLocator from '../dealerLocator/index.vue';

export default defineComponent({
  name: 'DealerLocatorSelector',
  components: {
    DealerLocator,
  },

  props: {
    allowedList: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    hiddenFields: {
      type: Array as PropType<Array<string>>,
      required: false,
    },
    vendors: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    interactive: {
      type: Boolean,
      default: false,
    },
    preload: {
      type: Boolean,
      default: false,
    },
    hasFilter: {
      type: Boolean,
      default: false,
    },
    applyAllowedListEnabled: {
      type: Boolean,
      default: true,
    },
    autoHeight: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: String as PropType<'lazy' | 'eager'>,
      default: 'lazy',
    },
  },

  emits: ['vendor-selected', 'vendor-selected-all'],

  setup(props, { emit }) {
    const availableVendors: Ref<Array<string>> = toRef(props, 'vendors');
    const showErrorMessage: Ref<boolean> = ref(false);
    const applyAllowedList: Ref<boolean> = ref(true);

    // computed
    const showApplyAllowedList: ComputedRef<boolean> = computed(() => {
      if (props.allowedList.length === 0 || !props.applyAllowedListEnabled) return false;

      const allowedList = intersection(props.allowedList, availableVendors.value);
      const vendors = allowedList.every((id: string) => availableVendors.value.includes(id))
        ? availableVendors.value
        : intersection(availableVendors.value, props.allowedList);

      return vendors.length !== allowedList.length;
    });

    const vendorList: ComputedRef<Array<string>> = computed(() => {
      if (applyAllowedList.value && props.allowedList.length > 0) {
        return intersection(props.allowedList, availableVendors.value);
      }

      return availableVendors.value;
    });

    // methods
    const dealerSelected = (dealer: any): void => {
      emit('vendor-selected', dealer.baseInfo.outletId);
      emit('vendor-selected-all', dealer);
    };

    return {
      // state
      showErrorMessage,
      applyAllowedList,
      // computed
      showApplyAllowedList,
      vendorList,
      // methods
      dealerSelected,
    };
  },
});
</script>

<style src="./dealerLocatorSelector.scss" lang="scss"></style>
